// @ts-ignore

import dayjs, { Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend((o, c) => {
  // todo support Wo (ISO week)
  const proto = c.prototype;
  const oldFormat = proto.format;
  proto.format = function f(formatStr) {
    const str = (formatStr || '').replace('Wo', 'wo');
    return oldFormat.bind(this)(str);
  };
});
const localeMap: Record<string, string> = {
  // ar_EG:
  // az_AZ:
  // bg_BG:
  bn_BD: 'bn-bd',
  by_BY: 'be',
  // ca_ES:
  // cs_CZ:
  // da_DK:
  // de_DE:
  // el_GR:
  en_GB: 'en-gb',
  en_US: 'en',
  // es_ES:
  // et_EE:
  // fa_IR:
  // fi_FI:
  fr_BE: 'fr',
  // todo: dayjs has no fr_BE locale, use fr at present
  fr_CA: 'fr-ca',
  // fr_FR:
  // ga_IE:
  // gl_ES:
  // he_IL:
  // hi_IN:
  // hr_HR:
  // hu_HU:
  hy_AM: 'hy-am',
  // id_ID:
  // is_IS:
  // it_IT:
  // ja_JP:
  // ka_GE:
  // kk_KZ:
  // km_KH:
  kmr_IQ: 'ku',
  // kn_IN:
  // ko_KR:
  // ku_IQ: // previous ku in antd
  // lt_LT:
  // lv_LV:
  // mk_MK:
  // ml_IN:
  // mn_MN:
  // ms_MY:
  // nb_NO:
  // ne_NP:
  nl_BE: 'nl-be',
  // nl_NL:
  // pl_PL:
  pt_BR: 'pt-br',
  // pt_PT:
  // ro_RO:
  // ru_RU:
  // sk_SK:
  // sl_SI:
  // sr_RS:
  // sv_SE:
  // ta_IN:
  // th_TH:
  // tr_TR:
  // uk_UA:
  // ur_PK:
  // vi_VN:
  zh_CN: 'zh-cn',
  zh_HK: 'zh-hk',
  zh_TW: 'zh-tw',
};
const parseLocale = function parseLocale(locale: string) {
  const mapLocale = localeMap[locale];
  return mapLocale || locale.split('_')[0];
};

/* istanbul ignore next */
const parseNoMatchNotice = function parseNoMatchNotice() {
  // zombieJ:
  // When user typing, its always miss match format.
  // This check is meaningless.
  // https://github.com/ant-design/ant-design/issues/51839
  // noteOnce(false, 'Not match any format. Please help to fire a issue about this.');
};
const generateConfig = {
  // get
  getNow: function getNow() {
    const now = dayjs();
    // https://github.com/ant-design/ant-design/discussions/50934
    if (typeof (now as any).tz === 'function') {
      return (now as any).tz(); // use default timezone
    }
    return now;
  },
  getFixedDate: function getFixedDate(string: string) {
    return dayjs(string, ['YYYY-M-DD', 'YYYY-MM-DD']);
  },
  getEndDate: function getEndDate(date: Dayjs) {
    return date.endOf('month');
  },
  getWeekDay: function getWeekDay(date: Dayjs) {
    const clone = date.locale('en');
    return clone.weekday() + clone.localeData().firstDayOfWeek();
  },
  getYear: function getYear(date: Dayjs) {
    return date.year();
  },
  getMonth: function getMonth(date: Dayjs) {
    return date.month();
  },
  getDate: function getDate(date: Dayjs) {
    return date.date();
  },
  getHour: function getHour(date: Dayjs) {
    return date.hour();
  },
  getMinute: function getMinute(date: Dayjs) {
    return date.minute();
  },
  getSecond: function getSecond(date: Dayjs) {
    return date.second();
  },
  getMillisecond: function getMillisecond(date: Dayjs) {
    return date.millisecond();
  },
  // set
  addYear: function addYear(date: Dayjs, diff: number) {
    return date.add(diff, 'year');
  },
  addMonth: function addMonth(date: Dayjs, diff: number) {
    return date.add(diff, 'month');
  },
  addDate: function addDate(date: Dayjs, diff: number) {
    return date.add(diff, 'day');
  },
  setYear: function setYear(date: Dayjs, year: number) {
    return date.year(year);
  },
  setMonth: function setMonth(date: Dayjs, month: number) {
    return date.month(month);
  },
  setDate: function setDate(date: Dayjs, num: number) {
    return date.date(num);
  },
  setHour: function setHour(date: Dayjs, hour: number) {
    return date.hour(hour);
  },
  setMinute: function setMinute(date: Dayjs, minute: number) {
    return date.minute(minute);
  },
  setSecond: function setSecond(date: Dayjs, second: number) {
    return date.second(second);
  },
  setMillisecond: function setMillisecond(date: Dayjs, milliseconds: number) {
    return date.millisecond(milliseconds);
  },
  // Compare
  isAfter: function isAfter(date1: Dayjs, date2: Dayjs) {
    return date1.isAfter(date2);
  },
  isValidate: function isValidate(date: Dayjs) {
    return date.isValid();
  },
  locale: {
    getWeekFirstDay: function getWeekFirstDay(locale: string) {
      return dayjs().locale(parseLocale(locale)).localeData().firstDayOfWeek();
    },
    getWeekFirstDate: function getWeekFirstDate(locale: string, date: Dayjs) {
      return date.locale(parseLocale(locale)).weekday(0);
    },
    getWeek: function getWeek(locale: string, date: Dayjs) {
      return date.locale(parseLocale(locale)).week();
    },
    getShortWeekDays: function getShortWeekDays(locale: string) {
      return dayjs().locale(parseLocale(locale)).localeData().weekdaysMin();
    },
    getShortMonths: function getShortMonths(locale: string) {
      return dayjs().locale(parseLocale(locale)).localeData().monthsShort();
    },
    format: function format(locale: string, date: Dayjs, _format: string) {
      return date.locale(parseLocale(locale)).format(_format);
    },
    parse: function parse(locale: string, text: string, formats: string[]) {
      const localeStr = parseLocale(locale);
      for (let i = 0; i < formats.length; i += 1) {
        const format = formats[i];
        const formatText = text;
        if (format.includes('wo') || format.includes('Wo')) {
          // parse Wo
          const year = formatText.split('-')[0];
          const weekStr = formatText.split('-')[1];
          const firstWeek = dayjs(year, 'YYYY')
            .startOf('year')
            .locale(localeStr);
          for (let j = 0; j <= 52; j += 1) {
            const nextWeek = firstWeek.add(j, 'week');
            if (nextWeek.format('Wo') === weekStr) {
              return nextWeek;
            }
          }
          parseNoMatchNotice();
          return null;
        }
        const date = dayjs(formatText, format, true).locale(localeStr);
        if (date.isValid()) {
          return date;
        }
      }
      if (text) {
        parseNoMatchNotice();
      }
      return null;
    },
  },
};
export default generateConfig;
